import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Add, PersonRemove } from "@mui/icons-material";
import { ApiError } from "@/api/client";
import {
  useAddAssignment,
  useParticipationInvestigators,
  useRemoveAssignment,
} from "@/api/hooks/participationInvestigators";
import { useInvestigators } from "@/api/hooks/investigators";
import {
  INVESTIGATOR_ROLES,
  INVESTIGATOR_ROLE_LABELS,
} from "@/api/labels";
import type {
  Assignment,
  InvestigatorListRow,
  InvestigatorRole,
  PIConflictResponse,
} from "@/api/types";
import ReasonPromptDialog from "@/components/documents/ReasonPromptDialog";
import { formatDate } from "@/utils/format";

interface Props {
  participationId: string;
}

function fullName(inv: InvestigatorListRow): string {
  return `${inv.first_name} ${inv.last_name}${inv.npi ? ` · NPI ${inv.npi}` : ""}`;
}

export default function InvestigatorsSection({ participationId }: Props) {
  const { data: assignments, isLoading } =
    useParticipationInvestigators(participationId);
  const { data: investigatorPool } = useInvestigators({ is_active: true });
  const addAssignment = useAddAssignment(participationId);
  const removeAssignment = useRemoveAssignment(participationId);

  const [addOpen, setAddOpen] = useState(false);
  const [pickerInv, setPickerInv] = useState<InvestigatorListRow | null>(null);
  const [pickerRole, setPickerRole] = useState<InvestigatorRole>("pi");
  const [addError, setAddError] = useState<string | null>(null);

  // 409 replace-PI state
  const [replaceCandidate, setReplaceCandidate] = useState<{
    newInvestigator: InvestigatorListRow;
    currentPiAssignmentId: string;
  } | null>(null);

  // Per-row remove
  const [removeTarget, setRemoveTarget] = useState<Assignment | null>(null);

  const pi = assignments?.find((a) => a.role === "pi");
  const subInvestigators = assignments?.filter(
    (a) => a.role === "sub_investigator",
  );

  const findInvestigator = (id: string): InvestigatorListRow | undefined =>
    investigatorPool?.find((p) => p.id === id);

  const resetAddForm = () => {
    setPickerInv(null);
    setPickerRole("pi");
    setAddError(null);
  };

  const submitAdd = async () => {
    if (!pickerInv) return;
    setAddError(null);
    try {
      await addAssignment.mutateAsync({
        investigator_id: pickerInv.id,
        role: pickerRole,
      });
      setAddOpen(false);
      resetAddForm();
    } catch (e) {
      // ApiError exposes status (number) and body (string). FastAPI wraps
      // the structured PIConflictResponse inside { "detail": <body> }, so
      // we parse twice: outer envelope, then the nested detail.
      if (e instanceof ApiError && e.status === 409 && pi) {
        try {
          const envelope = JSON.parse(e.body) as { detail?: PIConflictResponse };
          if (envelope.detail?.error === "PI_ALREADY_ASSIGNED") {
            setReplaceCandidate({
              newInvestigator: pickerInv,
              currentPiAssignmentId: pi.id,
            });
            setAddOpen(false);
            return;
          }
        } catch {
          // Fall through to generic error display.
        }
      }
      setAddError(e instanceof Error ? e.message : "Failed to assign");
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Investigators
          </Typography>
          <Button
            size="small"
            startIcon={<Add />}
            onClick={() => setAddOpen(true)}
          >
            Add investigator
          </Button>
        </Box>

        {isLoading && <CircularProgress size={20} />}

        <Stack spacing={2}>
          {/* PI block */}
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              border: 1,
              borderColor: pi ? "primary.main" : "divider",
              bgcolor: pi ? "primary.50" : "background.default",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Principal Investigator
            </Typography>
            {pi ? (
              <Stack
                direction="row"
                spacing={2}
                sx={{ mt: 0.5, alignItems: "center" }}
              >
                <Box sx={{ flex: 1 }}>
                  <Link
                    to={`/clinops/sites/investigators/${pi.investigator_id}`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <Typography variant="body1">
                      {findInvestigator(pi.investigator_id)
                        ? fullName(findInvestigator(pi.investigator_id)!)
                        : "(loading…)"}
                    </Typography>
                  </Link>
                  <Typography variant="caption" color="text.secondary">
                    Assigned {formatDate(pi.assigned_at)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setRemoveTarget(pi)}
                  aria-label="Remove PI"
                >
                  <PersonRemove fontSize="small" />
                </IconButton>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                No PI assigned.
              </Typography>
            )}
          </Box>

          {/* Sub-Is */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Sub-Investigators
            </Typography>
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              {(subInvestigators ?? []).map((a) => (
                <Stack
                  key={a.id}
                  direction="row"
                  spacing={2}
                  sx={{
                    p: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Link
                      to={`/clinops/sites/investigators/${a.investigator_id}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <Typography variant="body2">
                        {findInvestigator(a.investigator_id)
                          ? fullName(findInvestigator(a.investigator_id)!)
                          : "(loading…)"}
                      </Typography>
                    </Link>
                    <Typography variant="caption" color="text.secondary">
                      Assigned {formatDate(a.assigned_at)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => setRemoveTarget(a)}
                    aria-label="Remove Sub-I"
                  >
                    <PersonRemove fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              {(subInvestigators ?? []).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No Sub-Investigators assigned.
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      </CardContent>

      {/* Add modal */}
      <Dialog
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          resetAddForm();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add investigator</DialogTitle>
        <DialogContent>
          {addError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {addError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={investigatorPool ?? []}
              getOptionLabel={(o) => fullName(o)}
              value={pickerInv}
              onChange={(_, v) => setPickerInv(v)}
              renderInput={(p) => (
                <TextField {...p} label="Investigator" required />
              )}
            />
            <TextField
              select
              label="Role"
              value={pickerRole}
              onChange={(e) =>
                setPickerRole(e.target.value as InvestigatorRole)
              }
              required
            >
              {INVESTIGATOR_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {INVESTIGATOR_ROLE_LABELS[r]}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAddOpen(false);
              resetAddForm();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitAdd}
            disabled={!pickerInv}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Replace-PI prompt */}
      <ReasonPromptDialog
        open={replaceCandidate !== null}
        title="Replace Principal Investigator?"
        description={
          replaceCandidate
            ? `A PI is already assigned. Provide a reason and we'll record both events on the audit log: removing the current PI and assigning ${fullName(replaceCandidate.newInvestigator)}.`
            : ""
        }
        confirmLabel="Replace PI"
        confirmColor="warning"
        onClose={() => setReplaceCandidate(null)}
        onConfirm={async (reason) => {
          if (!replaceCandidate) return;
          await removeAssignment.mutateAsync({
            assignmentId: replaceCandidate.currentPiAssignmentId,
            payload: { reason },
          });
          await addAssignment.mutateAsync({
            investigator_id: replaceCandidate.newInvestigator.id,
            role: "pi",
          });
          setReplaceCandidate(null);
          resetAddForm();
        }}
      />

      {/* Per-row remove */}
      <ReasonPromptDialog
        open={removeTarget !== null}
        title="Remove this assignment?"
        description="The assignment is soft-removed; the audit trail keeps the record. The reason you provide is stored alongside the removed_at timestamp."
        confirmLabel="Remove"
        confirmColor="error"
        onClose={() => setRemoveTarget(null)}
        onConfirm={async (reason) => {
          if (!removeTarget) return;
          await removeAssignment.mutateAsync({
            assignmentId: removeTarget.id,
            payload: { reason },
          });
          setRemoveTarget(null);
        }}
      />
    </Card>
  );
}
